import { useMemo } from 'react'
import { Plus } from 'lucide-react'
import type { Competitor, BriefScope } from '@ispot/shared'
import { CategoryBand } from './CategoryBand'

interface CompetitorCategoryMapProps {
  competitors: Competitor[]
  onOpenBrief: (scope: BriefScope) => void
  onAddClick: () => void
}

const UNCATEGORIZED = 'Uncategorized'

function groupByCategory(competitors: Competitor[]): Array<[string, Competitor[]]> {
  const map = new Map<string, Competitor[]>()
  for (const competitor of competitors) {
    const cat = competitor.category?.trim() || UNCATEGORIZED
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(competitor)
  }
  // Larger categories first; Uncategorized always last
  return Array.from(map.entries()).sort(([a, aList], [b, bList]) => {
    if (a === UNCATEGORIZED) return 1
    if (b === UNCATEGORIZED) return -1
    if (bList.length !== aList.length) return bList.length - aList.length
    return a.localeCompare(b)
  })
}

export function CompetitorCategoryMap({
  competitors,
  onOpenBrief,
  onAddClick,
}: CompetitorCategoryMapProps) {
  const grouped = useMemo(() => groupByCategory(competitors), [competitors])

  const handleOpenCompetitor = (id: string) => onOpenBrief({ type: 'competitor', competitorId: id })
  const handleAddToCategory = (_category: string) => {
    // AddCompetitorModal reads existingCategories from the parent; no category preselect plumbing yet.
    onAddClick()
  }

  if (grouped.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '14px',
          padding: '32px',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: '16px',
            color: 'var(--color-text-secondary)',
            letterSpacing: '-0.01em',
          }}
        >
          No competitors tracked yet.
        </p>
        <p style={{ margin: 0, fontSize: '13px', maxWidth: '44ch' }}>
          Add a first competitor, or run <em>Discover</em> to let Skerpa propose an initial set from your uploaded research.
        </p>
        <button
          type="button"
          onClick={onAddClick}
          style={{
            marginTop: '6px',
            padding: '8px 16px',
            background: 'var(--color-accent)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Plus size={14} />
          Add competitor
        </button>
      </div>
    )
  }

  // ≤5 categories fit the viewport as equal-share rows; more → natural heights with scroll.
  const fitOnScreen = grouped.length <= 5
  const gridStyle: React.CSSProperties = fitOnScreen
    ? {
        display: 'grid',
        gridTemplateRows: `repeat(${grouped.length}, minmax(0, 1fr))`,
        rowGap: 0,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
      }

  return (
    <section
      aria-label="Competitive landscape"
      style={{
        flex: 1,
        padding: '8px 24px 24px',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={gridStyle}>
        {grouped.map(([category, list], idx) => (
          <CategoryBand
            key={category}
            category={category}
            competitors={list}
            isFirst={idx === 0}
            onOpenCompetitor={handleOpenCompetitor}
            onAddToCategory={handleAddToCategory}
          />
        ))}
      </div>
    </section>
  )
}
